import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import PublicLayout from "../../components/layout/PublicLayout";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Loader from "../../components/common/Loader";
import ErrorMessage from "../../components/common/ErrorMessage";
import Modal from "../../components/common/Modal";
import Input from "../../components/common/Input";
import contestService from "../../api/services/contestService";
import voteService from "../../api/services/voteService";
import paymentService from "../../api/services/paymentService";
import { useToast } from "../../context/ToastContext";
import {
  handleApiError,
  formatCurrency,
  calculatePercentage,
} from "../../utils/helpers";
import { getImageUrl } from '../../utils/imageHelper';
import LeaderboardCategoryModal from "../../components/common/Leaderboardcategorymodal";
import CategoryContestantsModal from "../../components/common/Categorycontestantsmodal";

// ─── Rank helpers (unchanged) ─────────────────────────────────────────────────
const getRankSuffix = (rank) => {
  if (rank === 1) return "st";
  if (rank === 2) return "nd";
  if (rank === 3) return "rd";
  return "th";
};

const getRankColor = (rank) => {
  if (rank === 1) return "from-yellow-400 to-yellow-600";
  if (rank === 2) return "from-gray-300 to-gray-500";
  if (rank === 3) return "from-orange-400 to-orange-600";
  return "from-blue-400 to-blue-600";
};

const getRankBadgeColor = (rank) => {
  if (rank === 1) return "bg-gradient-to-br from-yellow-400 to-yellow-600 text-white";
  if (rank === 2) return "bg-gradient-to-br from-gray-300 to-gray-500 text-white";
  if (rank === 3) return "bg-gradient-to-br from-orange-400 to-orange-600 text-white";
  return "bg-gradient-to-br from-blue-500 to-blue-600 text-white";
};

const ContestDetailPage = () => {
  const { contestId } = useParams();
  const location = useLocation();
  const { success, error: showError } = useToast();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voteModalOpen, setVoteModalOpen] = useState(false);
  const [selectedContestant, setSelectedContestant] = useState(null);
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const [leaderboardModalOpen, setLeaderboardModalOpen] = useState(false);
  const [selectedLeaderboardCategory, setSelectedLeaderboardCategory] = useState(null);
  const [categoryContestantsModalOpen, setCategoryContestantsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchContestDetail();
    const pendingPayment = sessionStorage.getItem('pendingPayment');
    if (pendingPayment) {
      try {
        const paymentData = JSON.parse(pendingPayment);
        if (paymentData.contestId === contestId) {
          success(`Payment completed! ${paymentData.voteCount} vote(s) cast for ${paymentData.contestantName}`);
          sessionStorage.removeItem('pendingPayment');
          fetchContestDetail();
        }
      } catch (e) {
        console.error('Error processing pending payment:', e);
      }
    }
  }, [contestId]);

  useEffect(() => {
    const interval = setInterval(() => { fetchContestDetail(); }, 10000);
    return () => clearInterval(interval);
  }, [contestId]);

  const fetchContestDetail = async () => {
    try {
      if (!contest) setLoading(true);
      setError(null);
      const response = await contestService.getPublicContestDetail(contestId);
      if (response.success) setContest(response.data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleLeaderboardCategoryClick = (category) => {
    setSelectedLeaderboardCategory(category);
    setLeaderboardModalOpen(true);
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setCategoryContestantsModalOpen(true);
  };

  const handleVoteClick = (contestant, categoryId) => {
    console.log("Before setState - categoryId:", categoryId);
    const contestantWithCategory = { ...contestant, categoryId };
    console.log("Contestant object being set:", contestantWithCategory);
    setSelectedContestant(contestantWithCategory);
    setVoteModalOpen(true);
  };

  const handleVoteSuccess = async () => {
    setVoteModalOpen(false);
    setSelectedContestant(null);
    setTimeout(() => { fetchContestDetail(); }, 500);
  };

  console.log("Contestant Data:", selectedContestant);

  if (loading && !contest) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <Loader size="lg" text="Loading contest..." />
        </div>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <ErrorMessage message={error} onRetry={fetchContestDetail} />
        </div>
      </PublicLayout>
    );
  }

  if (!contest) return null;

  const totalVotes =
    contest.categories?.reduce((acc, category) =>
      acc + category.contestants.reduce((sum, c) => sum + (c.voteCount || 0), 0), 0
    ) || 0;

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">

        {/* ── Contest Header (unchanged) ────────────────────────────────── */}
        <div className="mb-8 sm:mb-12">
          {contest.banner && !bannerError ? (
            <div className="w-full h-48 sm:h-64 lg:h-80 rounded-xl sm:rounded-2xl overflow-hidden mb-6 sm:mb-8 shadow-xl sm:shadow-2xl relative group">
              <img
                src={getImageUrl(contest.banner)}
                alt={contest.title}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                onError={() => setBannerError(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
          ) : contest.banner ? (
            <div className="w-full h-48 sm:h-64 lg:h-80 rounded-xl sm:rounded-2xl overflow-hidden mb-6 sm:mb-8 bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
              <svg className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          ) : null}

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">{contest.title}</h1>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">{contest.description}</p>
              <div className="flex items-center bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200">
                {contest.host?.logo && !logoError ? (
                  <img
                    src={getImageUrl(contest.host.logo)}
                    alt={contest.host.name}
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full mr-3 sm:mr-4 border-2 border-red-200 shadow-md object-cover flex-shrink-0"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mr-3 sm:mr-4 shadow-md flex-shrink-0">
                    <span className="text-white font-bold text-lg sm:text-xl">{contest.host?.name?.charAt(0) || "H"}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Hosted by</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900 truncate">{contest.host?.name}</p>
                  {contest.host?.description && (
                    <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">{contest.host.description}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-5 sm:p-6 rounded-xl sm:rounded-2xl border-2 border-red-200 text-center shadow-lg w-full lg:w-auto lg:min-w-[200px]">
              <p className="text-sm text-gray-600 mb-2">Vote Price</p>
              <p className="text-3xl sm:text-4xl font-bold text-red-600 mb-1">
                {contest.votePrice > 0 ? formatCurrency(contest.votePrice) : "FREE"}
              </p>
              <p className="text-xs sm:text-sm text-gray-500">per vote</p>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            LIVE LEADERBOARD — clean category list, tap to see details
        ══════════════════════════════════════════════════════════════════ */}
        {contest.categories?.length > 0 && (
          <div className="mb-8 sm:mb-12">
            <Card className="shadow-2xl overflow-hidden border-2 border-gray-200">

              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-3xl font-bold text-white leading-tight">Live Leaderboard</h2>
                    <p className="text-xs sm:text-sm text-red-100 mt-0.5">Tap a category to view rankings</p>
                  </div>
                </div>
              </div>

              {/* Category list — one clean row per category */}
              <div className="divide-y divide-gray-100">
                {contest.categories.map((category, catIndex) => {
                  const totalCategoryVotes = category.contestants.reduce(
                    (sum, c) => sum + (c.voteCount || 0), 0
                  );
                  const contestantCount = category.contestants.length;

                  return (
                    <button
                      key={category.id}
                      onClick={() => handleLeaderboardCategoryClick(category)}
                      className="w-full text-left bg-white hover:bg-red-50 active:bg-red-100 transition-colors duration-150 px-4 py-4 sm:px-6 sm:py-5 flex items-center gap-3 sm:gap-4"
                    >
                      {/* Category number */}
                      <span className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-600 text-white text-sm font-bold flex-shrink-0">
                        {catIndex + 1}
                      </span>

                      {/* Category info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-bold text-gray-900 truncate">
                          {category.name}
                        </p>
                        {/* <p className="text-xs text-gray-400 mt-0.5">
                          {contestantCount} contestant{contestantCount !== 1 ? 's' : ''}
                          {totalCategoryVotes > 0 && (
                            <span> · {totalCategoryVotes.toLocaleString()} votes</span>
                          )}
                        </p> */}
                      </div>

                      {/* "View rankings" + arrow */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs text-red-500 font-medium hidden sm:inline">
                          View rankings
                        </span>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* ── Categories — vote section (unchanged) ────────────────────── */}
        {contest.categories?.map((category, categoryIndex) => (
          <div key={category.id} className="mb-6 sm:mb-8">
            <Card className="shadow-xl overflow-hidden border-2 border-gray-200">
              <button
                onClick={() => handleCategoryClick(category)}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 p-4 sm:p-6 flex items-center justify-between hover:from-red-700 hover:to-red-800 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h2 className="text-xl sm:text-2xl font-bold text-white">{category.name}</h2>
                    <p className="text-sm text-red-100 mt-1">
                      {category.contestants?.length || 0} contestants · click to vote
                    </p>
                  </div>
                </div>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </Card>
          </div>
        ))}

        {/* ── Modals (all unchanged) ────────────────────────────────────── */}
        <LeaderboardCategoryModal
          isOpen={leaderboardModalOpen}
          onClose={() => { setLeaderboardModalOpen(false); setSelectedLeaderboardCategory(null); }}
          category={selectedLeaderboardCategory}
        />

        <CategoryContestantsModal
          isOpen={categoryContestantsModalOpen}
          onClose={() => { setCategoryContestantsModalOpen(false); setSelectedCategory(null); }}
          category={selectedCategory}
          totalVotes={totalVotes}
          onVoteClick={handleVoteClick}
        />

        {selectedContestant && (
          <VoteModal
            isOpen={voteModalOpen}
            onClose={() => { setVoteModalOpen(false); setSelectedContestant(null); }}
            contestant={selectedContestant}
            contest={contest}
            onSuccess={handleVoteSuccess}
          />
        )}
      </div>
    </PublicLayout>
  );
};

// ─── VoteModal (completely unchanged) ────────────────────────────────────────
const VoteModal = ({ isOpen, onClose, contestant, contest, onSuccess }) => {
  const { success, error: showError } = useToast();
  const [formData, setFormData] = useState({
    voteCount: 1,
    voterName: "",
    voterEmail: "",
    voterPhone: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [photoError, setPhotoError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.voteCount || formData.voteCount < 1) newErrors.voteCount = "Vote count must be at least 1";
    if (!formData.voterPhone) newErrors.voterPhone = "Phone is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const voteData = {
        contestId: contest.id,
        categoryId: contestant?.categoryId,
        contestantId: contestant.id,
        voteCount: parseInt(formData.voteCount, 10),
        voterName: formData.voterName.trim(),
        voterEmail: formData.voterEmail.trim(),
        voterPhone: formData.voterPhone.trim(),
      };

      console.log("Vote Data being sent:", voteData);

      if (!voteData.categoryId) {
        showError("Category ID is missing. Please try again.");
        setLoading(false);
        return;
      }

      const response = await voteService.castVote(voteData);

      if (response.success) {
        if (contest.votePrice > 0 && response.data.status === "PENDING") {
          const paymentData = { voteId: response.data.id, phoneNumber: formData.voterPhone };
          const paymentResponse = await paymentService.initializePayment(paymentData);

          if (paymentResponse.success) {
            const totalAmount = (formData.voteCount || 0) * contest.votePrice;
            sessionStorage.setItem('pendingPayment', JSON.stringify({
              reference: paymentResponse.data.reference,
              voteId: response.data.id,
              contestId: contest.id,
              contestantName: contestant.name,
              voteCount: formData.voteCount,
              amount: totalAmount,
              timestamp: Date.now(),
            }));
            success("Redirecting to payment...");
            setTimeout(() => { window.location.href = paymentResponse.data.authorizationUrl; }, 100);
          }
        } else {
          success("Vote cast successfully!");
          onSuccess();
        }
      }
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = (formData.voteCount || 0) * contest.votePrice;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cast Your Vote" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg sm:rounded-xl border-2 border-red-200">
          <div className="flex items-center">
            {contestant.photo && !photoError ? (
              <img
                src={getImageUrl(contestant.photo)}
                alt={contestant.name}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover mr-3 sm:mr-4 border-2 sm:border-4 border-white shadow-lg flex-shrink-0"
                onError={() => setPhotoError(true)}
              />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mr-3 sm:mr-4 border-2 sm:border-4 border-white shadow-lg flex-shrink-0">
                <span className="text-white font-bold text-lg sm:text-xl">{contestant.name?.charAt(0) || "C"}</span>
              </div>
            )}
            <div className="min-w-0">
              <h4 className="font-bold text-base sm:text-lg text-gray-900 truncate">{contestant.name}</h4>
              {contestant.contestantCode && (
                <p className="text-xs sm:text-sm text-gray-600 font-semibold mt-0.5">Code: #{contestant.contestantCode}</p>
              )}
              <p className="text-xs sm:text-sm text-red-600 font-semibold mt-1">
                {formatCurrency(contest.votePrice)} per vote
              </p>
            </div>
          </div>
        </div>

        <Input label="Number of Votes" type="number" name="voteCount" value={formData.voteCount} onChange={handleChange} min="1" error={errors.voteCount} required />
        <Input label="Your Name (Optional)" name="voterName" value={formData.voterName} onChange={handleChange} placeholder="John Doe" error={errors.voterName} />
        <Input label="Email Address (Optional)" type="email" name="voterEmail" value={formData.voterEmail} onChange={handleChange} placeholder="john@example.com" error={errors.voterEmail} />
        <Input label="Phone Number" type="tel" name="voterPhone" value={formData.voterPhone} onChange={handleChange} placeholder="+233241234567" error={errors.voterPhone} required />

        {contest.votePrice > 0 && (
          <div className="mb-4 sm:mb-6 p-4 sm:p-5 bg-gradient-to-r from-red-50 to-red-100 rounded-lg sm:rounded-xl border-2 border-red-300">
            <div className="flex items-center justify-between">
              <span className="text-sm sm:text-base text-gray-700 font-medium">Total Amount:</span>
              <span className="text-2xl sm:text-3xl font-bold text-red-600">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1 w-full">Cancel</Button>
          <Button type="submit" variant="primary" loading={loading} className="flex-1 w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800">
            {contest.votePrice > 0 ? "Proceed to Payment" : "Cast Vote"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ContestDetailPage;